import { type FacetInspection, type FacetKey, type GoalEnvelope, type GoalsInspection } from '@/lib/api';
import { GOALS_COPY, readsAsSampleLine } from '@/lib/rankingTerms';
import { FACET_KEYS, normalizeGoals } from '@/lib/goals';
import { ScoreTerm } from '@/components/ScoreTerm';
import { FacetTargetInput } from '@/components/GoalsFields';


/** Facet scores live on a 0-10 scale (docs/RANKING_TERMS.md); only the DRAWING is bounded to it. */
const FACET_SCALE_MAX = 10;

const pct = (v: number) => `${Math.max(0, Math.min(100, (v / FACET_SCALE_MAX) * 100))}%`;

function sourceLabel(f: FacetInspection): string {
  if (f.source === 'goal') return GOALS_COPY.sourceGoal;
  if (f.source === 'fence_median') return GOALS_COPY.sourceMedian;
  return GOALS_COPY.sourceNone;
}

/** The facet's p10-p90 region as a shaded band with a p50 tick, plus the value and target markers. */
function FacetBar({ facet, k }: { facet: FacetInspection; k: FacetKey }) {
  const { fence } = facet;
  return (
    <div className="relative h-3 rounded-full flex-1 min-w-[120px]" style={{ background: 'var(--surface-container-high)' }}>
      {fence && (
        <>
          <div data-testid={`facet-region-${k}`} className="absolute top-0 h-full rounded-full"
               style={{ left: pct(fence.p10), width: `calc(${pct(fence.p90)} - ${pct(fence.p10)})`, background: 'var(--primary)', opacity: 0.3 }} />
          <div data-testid={`facet-p50-${k}`} className="absolute top-[-2px] h-[16px] w-[2px]"
               style={{ left: pct(fence.p50), background: 'var(--primary)' }} />
        </>
      )}
      <div className="absolute top-[1px] h-[10px] w-[10px] rounded-full -translate-x-1/2"
           style={{ left: pct(facet.value), background: 'var(--on-surface)' }} />
      {facet.target !== null && (
        <div className="absolute top-[-3px] h-[18px] w-[2px]" style={{ left: pct(facet.target), background: 'var(--warning-text)' }} />
      )}
    </div>
  );
}

export function FacetRow({ k, facet, stated, editable, onEdit }: {
  k: FacetKey; facet: FacetInspection; stated: number | undefined; editable: boolean; onEdit: (raw: string) => void;
}) {
  const { fence } = facet;
  const outside = facet.source === 'goal' && facet.target !== null && fence !== null
    && (facet.target < fence.p10 || facet.target > fence.p90);
  return (
    <div data-testid={`facet-row-${k}`} className="flex flex-col gap-1 py-2 border-b border-outline-variant last:border-b-0">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-28 shrink-0"><ScoreTerm term={k} /></div>
        <div data-testid={`facet-value-${k}`} className="w-12 shrink-0 tabular-nums text-sm">{facet.value.toFixed(1)}</div>
        <FacetBar facet={facet} k={k} />
        {editable
          ? <FacetTargetInput facet={k} value={stated} placeholder={facet.target} onChange={onEdit} />
          : <div className="w-24 tabular-nums text-sm">{facet.target !== null ? facet.target.toFixed(1) : '—'}</div>}
        <div data-testid={`facet-gap-${k}`} className="w-20 shrink-0 tabular-nums text-sm text-on-surface-variant">
          {GOALS_COPY.gapLine(facet.target !== null ? facet.gap.toFixed(1) : null)}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs text-on-surface-variant pl-1">
        <span data-testid={`facet-source-${k}`}>{sourceLabel(facet)}</span>
        {fence
          ? (
            <span data-testid={`facet-n-${k}`}>
              {`n=${fence.n}`}{fence.low_confidence ? ` · ${GOALS_COPY.lowConfidence}` : ''}
            </span>
          )
          : <span>{GOALS_COPY.noFence}</span>}
        {outside && <span className="text-warning-text">{GOALS_COPY.outsideRegion}</span>}
        {facet.degraded && <span className="text-warning-text">{GOALS_COPY.degraded}</span>}
      </div>
    </div>
  );
}

/** A fit measured outside the card (e.g. a Dec-a-Sketch result): the inspection fields minus the build identity. */
export type FitMeasure = Omit<GoalsInspection, 'build_id' | 'trial' | 'goals'>;

/** The read-only fit: facet rows, threshold, budget lines and price. No editing, no trial, no credit. */
export function FitReadout({ fit, goals }: { fit: FitMeasure; goals: GoalEnvelope | null }) {
  const stated = normalizeGoals(goals).facet_targets;
  return (
    <div data-testid="fit-readout" className="flex flex-col gap-2">
      {fit.advisory && (
        <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
          <ScoreTerm term="reads_as" extra={readsAsSampleLine(fit.advisory.n, fit.advisory.bracket)} />
          {GOALS_COPY.readsAs(fit.advisory.bracket, fit.advisory.position)}
        </div>
      )}
      {fit.degraded && <div className="text-xs text-warning-text">{GOALS_COPY.degraded}</div>}
      <div>
        {FACET_KEYS.map((k) => (
          <FacetRow key={k} k={k} facet={fit.facets[k]} stated={stated[k]} editable={false} onEdit={() => {}} />
        ))}
      </div>
      <ThresholdLine insp={fit} />
      <div className="flex items-center gap-2 text-sm text-on-surface-variant flex-wrap">
        <ScoreTerm term="budget" /> <span>{fit.budget_fit}</span>
        <ScoreTerm term="salt" /> <span>{fit.salt_fit}</span>
        <ScoreTerm term="deck_budget" /> <span>{fit.deck_budget_fit}</span>
      </div>
    </div>
  );
}

export function ThresholdLine({ insp }: { insp: Pick<GoalsInspection, 'threshold_fit' | 'operational_threshold' | 'engines_online_turn'> }) {
  const { threshold_fit: fit, operational_threshold: limit, engines_online_turn: turn } = insp;
  if (fit === null || limit === null) return null;
  const text = fit === 'unknown' ? GOALS_COPY.thresholdUnknown
    : fit === 'late' ? GOALS_COPY.thresholdLate(turn, limit)
    : GOALS_COPY.thresholdOk(turn, limit);
  return (
    <div data-testid="threshold-fit" className="flex items-center gap-2 text-sm">
      <ScoreTerm term="engines_online" />
      <span className={fit === 'late' ? 'text-warning-text' : 'text-on-surface-variant'}>{text}</span>
    </div>
  );
}
